import Axios from 'axios'
import { getIdToken } from 'firebase/auth'

import {
  ChangeTranscriptLanguageRequest,
  GenerateResumableUploadUrlRequest,
  GenerateResumableUploadUrlResponse,
  GetAtsSyncStatsResponse,
  GetCustomSummaryPromptsParams,
  GetGoogleAuthUrlQuery,
  GetInterviewFilterOptionsParams,
  GetShortInterviewsRequestParams,
  GetTeamCustomSummaryPromptsParams,
  ListATSJobsResponse,
  StartTranscriptionFromGCSRequest,
  UpdateSummaryPartScoreRequest,
  UpdateSummaryPartScoreResponse,
} from '@pkg/types/api'

import { auth } from '@/firebase'

import { IApi } from './types'

const baseURL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080/api'

export const axios = Axios.create({
  baseURL,
})

export const gcsAxios = Axios.create({
  validateStatus: (status) => (status >= 200 && status < 300) || status === 308,
  headers: {
    'Referrer-Policy': 'no-referrer',
  },
})

axios.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser
      if (currentUser && config.headers) {
        config.headers.token = await getIdToken(currentUser)
      }
    } catch (err) {
      console.error(err)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

const api: IApi = {
  async checkVersion() {
    return await axios.post('/version')
  },

  async isEmailTemporary(data) {
    return await axios.post('/users/is-email-temporary', data)
  },

  async createUser(data) {
    return await axios.post('/users', data)
  },

  async deleteUser() {
    return await axios.delete('/users')
  },

  async updateUser(data) {
    return await axios.patch('/users', data)
  },

  async getUser() {
    return await axios.get('/users')
  },

  async getUserBillingInfo() {
    return await axios.get('/users/billing')
  },

  async getLinkedInProfile(profileURL: string) {
    return await axios.get(`/linkedin/${profileURL}`)
  },

  async getUserRoles() {
    return await axios.get('/roles')
  },

  async getRole(roleId: string) {
    return await axios.get(`/roles/${roleId}`)
  },

  async createRole(data) {
    return await axios.post('/roles', data)
  },

  async updateRole(data) {
    return await axios.patch('/roles', data)
  },

  async generateCompanyDescription(data) {
    return await axios.post('/companies/generate-description', data)
  },

  async getCompanies() {
    return await axios.get('/companies')
  },

  async getCompany(companyId: string) {
    return await axios.get(`/companies/${companyId}`)
  },

  async createCompany(data) {
    return await axios.post('/companies', data)
  },

  async updateCompany(data) {
    return await axios.patch('/companies', data)
  },

  async regenerateShortTranscript(interviewId: string) {
    return await axios.post(
      `/interviews/regenerate-short-transcript/${interviewId}`,
    )
  },

  async regenerateInterviewSummary(interviewId: string) {
    return await axios.post(`/interviews/regenerate-summary/${interviewId}`)
  },

  async generateSummaryPart(
    interviewId: string,
    data: { partName?: string; customSummaryPartPromptId?: string },
  ) {
    return await axios.post(
      `/interviews/generate-summary-part/${interviewId}`,
      data,
    )
  },

  async changeTranscriptLanguage(
    interviewId: string,
    data: ChangeTranscriptLanguageRequest,
  ) {
    return await axios.post(
      `/interviews/change-transcript-language/${interviewId}`,
      data,
    )
  },

  async parseCandidateCV(data) {
    return await axios.post('/completions/parse-cv', data)
  },

  async getCandidate(id: string) {
    return await axios.get(`/candidates/${id}`)
  },

  async createCandidate(data) {
    return await axios.post('/candidates', data)
  },

  async updateCandidate(data) {
    return await axios.patch('/candidates', data)
  },

  async getInterview(id: string) {
    return await axios.get(`/interviews/${id}`)
  },

  async getRecordingUrl(interviewId: string) {
    return await axios.get(`/interviews/${interviewId}/recording-url`)
  },

  async createInterview(data) {
    return await axios.post('/interviews', data)
  },

  async updateInterview(id, data) {
    return await axios.patch(`/interviews/${id}`, data)
  },

  async getShortInterviews({
    page,
    limit,
    ...filters
  }: GetShortInterviewsRequestParams) {
    return await axios.get('/interviews', {
      params: { page, limit, ...filters },
    })
  },

  async getInterviewFilterOptions(query: GetInterviewFilterOptionsParams) {
    return await axios.get('/interviews/filter-options', {
      params: query,
    })
  },

  async deleteInterview(interviewId: string, reason?: string) {
    return await axios.delete(`/interviews/${interviewId}`, {
      params: reason ? { reason } : undefined,
    })
  },

  async addInterviewParticipant(interviewId: string, data) {
    return await axios.post(`/interviews/${interviewId}/participants`, data)
  },

  async removeInterviewParticipant(interviewId: string, data) {
    return await axios.delete(`/interviews/${interviewId}/participants`, {
      data,
    })
  },

  async uploadCandidatePicture(data) {
    return await axios.post('/candidates/upload-picture', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  async uploadCandidateCV(data) {
    return await axios.post('/candidates/upload-cv', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  async deleteCandidateCV(candidateId, cvId) {
    return await axios.delete(`/candidates/${candidateId}/cv/${cvId}`)
  },

  async createPortalSession() {
    return await axios.post('/stripe/create-portal-session')
  },

  async addCustomSummaryPartPrompt(interviewId: string, data) {
    return await axios.post(
      `/interviews/add-custom-summary-prompt/${interviewId}`,
      data,
    )
  },

  async updateCustomSummaryPartPrompt(id, data) {
    return await axios.patch(`/interviews/custom-summary-prompt/${id}`, data)
  },

  async updateSummaryParts(data) {
    return await axios.patch('/interviews/update-summary-parts', data)
  },

  async deleteSummaryPart({ partId }) {
    return await axios.delete(`/interviews/summary-part/${partId}`)
  },

  async updateSummaryPartScore(
    partId: string,
    data: UpdateSummaryPartScoreRequest,
  ): Promise<UpdateSummaryPartScoreResponse> {
    const response = await axios.patch(
      `/interviews/summary-part/${partId}/score`,
      data,
    )
    return response.data
  },

  async archiveCustomSummaryPrompt(promptId: string) {
    return await axios.post(`/custom-summary-prompts/${promptId}/archive`)
  },

  async createCheckoutSession(data) {
    return await axios.post('/stripe/create-checkout-session', data)
  },

  async cancelTranscription(interviewId: string) {
    return await axios.post(`/transcription/cancel/${interviewId}`)
  },

  async sendEmail(data) {
    return await axios.post('/email', data)
  },

  async sendReferralInvitation(data) {
    return await axios.post('/email/referral-invitation', data)
  },

  async getPrices() {
    return await axios.get('/stripe/prices')
  },

  async getTeamMembers() {
    return await axios.get('/team-members')
  },

  async removeTeamMember(data) {
    return await axios.post('/team-members/remove', data)
  },

  async updateTeamMemberRole(data) {
    return await axios.post('/team-members/update-role', data)
  },

  async sendTeamInvite(data) {
    return await axios.post('/team-invite/send', data)
  },

  async cancelTeamInvite(teamInviteId: string) {
    return await axios.delete(`/team-invite/${teamInviteId}`)
  },

  async acceptTeamInvite(teamInviteId: string) {
    return await axios.post(`/team-invite/accept/${teamInviteId}`)
  },

  async declineTeamInvite(teamInviteId: string) {
    return await axios.post(`/team-invite/decline/${teamInviteId}`)
  },

  async getTeamInvite(teamInviteId: string) {
    return await axios.get(`/team-invite/${teamInviteId}`)
  },

  async updateTeamInviteRole(data) {
    return await axios.post(
      `/team-invite/update-role/${data.teamInviteId}`,
      data,
    )
  },

  async getAllSentTeamInvites() {
    return await axios.get('/team-invite/sent-invites')
  },

  async getAllReceivedTeamInvites() {
    return await axios.get('/team-invite/received-invites')
  },

  async getTeam() {
    return await axios.get('/team')
  },

  async createTeam(data) {
    return await axios.post('/team', data)
  },

  async getTeamCreationBillingPreview() {
    return await axios.get('/team/creation-billing-preview')
  },

  async updateTeam(data) {
    return await axios.patch('/team', data)
  },

  async leaveTeam() {
    return await axios.post('/team/leave')
  },

  async getTeamBillingInfo() {
    return await axios.get('/team/billing')
  },

  async googleAuth(params: GetGoogleAuthUrlQuery) {
    return await axios.get('/google/auth', { params })
  },

  async googleLogOut() {
    return await axios.post('/google/log-out')
  },

  async recordingOptOut(id: string) {
    return await axios.post(`/recording-optout/optout/${id}`)
  },

  async getUserSettings() {
    return await axios.get('/user-settings')
  },

  async updateUserSettings(data) {
    return await axios.patch('/user-settings', data)
  },

  async sendTestOptoutEmail() {
    return await axios.post('/recording-optout/test-email')
  },

  async uploadUserPicture(data) {
    return await axios.post('/users/upload-picture', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  async generateResumableUploadUrl(data: GenerateResumableUploadUrlRequest) {
    return await axios.post<GenerateResumableUploadUrlResponse>(
      '/transcription/generate-upload-url',
      data,
    )
  },

  async startTranscriptionFromGCS(data: StartTranscriptionFromGCSRequest) {
    return await axios.post('/transcription/start-from-gcs', data)
  },

  async getCustomSummaryPrompts(query: GetCustomSummaryPromptsParams) {
    return await axios.get('/custom-summary-prompts', { params: query })
  },

  async getTeamCustomSummaryPrompts(query: GetTeamCustomSummaryPromptsParams) {
    return await axios.get('/custom-summary-prompts/team', { params: query })
  },

  async handleAtsCallback(data) {
    return await axios.post('/ats/connect', data)
  },

  async disconnectAtsIntegration() {
    return await axios.delete('/ats/disconnect')
  },

  async searchATSCandidates(query) {
    return await axios.get('/ats/candidates', {
      params: { q: query },
    })
  },

  async listATSJobs(name?: string) {
    return await axios.get<ListATSJobsResponse>('/ats/jobs', {
      params: name ? { name } : undefined,
    })
  },

  async getAtsSyncStats() {
    return await axios.get<GetAtsSyncStatsResponse>('/ats/sync-stats')
  },

  async triggerAtsUserSync() {
    return await axios.post('/ats/sync-users')
  },

  // Public API management
  async getApiKeys() {
    return await axios.get('/v1/api-keys')
  },

  async createApiKey(data) {
    return await axios.post('/v1/api-keys', data)
  },

  async revokeApiKey(keyId: string) {
    return await axios.delete(`/v1/api-keys/${keyId}`)
  },

  async getWebhooks() {
    return await axios.get('/v1/webhooks')
  },

  async createWebhook(data) {
    return await axios.post('/v1/webhooks', data)
  },

  async updateWebhook(webhookId: string, data) {
    return await axios.patch(`/v1/webhooks/${webhookId}`, data)
  },

  async deleteWebhook(webhookId: string) {
    return await axios.delete(`/v1/webhooks/${webhookId}`)
  },
}

export default api
